import PaymentInstructions from '@/features/checkout/components/PaymentInstructions';
import ProofUploader from '@/features/checkout/components/ProofUploader';
import { API_BASE_URL, api } from '@/lib/api';
import { formatCurrency, formatDate, formatSeatIdentifier } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import type { PurchaseWithTickets } from '@eventflow/shared-types';
import { Clock, Key } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default function PurchaseConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const [purchase, setPurchase] = useState<PurchaseWithTickets | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [_proofUploaded, setProofUploaded] = useState(false);

  // Obtener información de cuenta creada desde el state de navegación
  const accountCreated = location.state?.accountCreated as
    | {
      email: string;
      password: string;
      message: string;
    }
    | undefined;

  useEffect(() => {
    if (!id) {
      navigate('/eventos');
      return;
    }

    const fetchPurchase = async () => {
      try {
        setIsLoading(true);
        const data = await api.get<PurchaseWithTickets>(`/purchases/${id}`);
        setPurchase(data);
        setProofUploaded(!!data.payment_proof_url);
      } catch (err) {
        // Manejo silencioso para compras no encontradas
        const message = err instanceof Error ? err.message : '';
        const notFound = /compra no encontrado/i.test(message) || /not found/i.test(message);
        if (notFound) {
          setError('Compra no encontrada');
        } else {
          setError('No se pudo cargar la información de la compra');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchase();
  }, [id, navigate]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            ✓ Confirmado
          </span>
        );
      case 'pending':
        return (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
      case 'failed':
        return (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            ✗ Rechazado
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Cancelado
          </span>
        );
      default:
        return null;
    }
  };

  const handleProofUpload = (proofUrl: string) => {
    setProofUploaded(true);
    if (purchase) {
      setPurchase({
        ...purchase,
        payment_proof_url: proofUrl,
      });
    }
  };

  const handleDownloadTickets = async () => {
    if (!id) return;

    try {
      // Construir URL del endpoint
      const url = `${API_BASE_URL}/purchases/${id}/tickets`;

      // Abrir en nueva ventana para descargar
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading tickets:', err);
      alert('Error al descargar los tickets. Por favor, intenta nuevamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando información de compra...</p>
        </div>
      </div>
    );
  }

  if (error || !purchase) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-4 text-red-600">{error || 'Compra no encontrada'}</p>
          <button onClick={() => navigate('/eventos')} className="btn-primary mt-4">
            Volver a Eventos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Account Created Alert */}
      {accountCreated && (
        <div className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-lg p-6 shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-xl font-bold text-green-900 mb-2">
                ¡Cuenta Creada Exitosamente!
              </h3>
              <p className="text-green-800 mb-4">{accountCreated.message}</p>

              <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Tus credenciales de acceso:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Email / Usuario</p>
                      <p className="text-sm font-mono font-bold text-gray-900 mt-1">
                        {accountCreated.email}
                      </p>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(accountCreated.email)}
                      className="ml-4 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      title="Copiar email"
                    >
                      Copiar
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Contraseña</p>
                      <p className="text-sm font-mono font-bold text-gray-900 mt-1">
                        {accountCreated.password}
                      </p>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(accountCreated.password)}
                      className="ml-4 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      title="Copiar contraseña"
                    >
                      Copiar
                    </button>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="text-xs text-yellow-800">
                      <strong>Importante:</strong> Guarda estas credenciales en un lugar seguro.
                      Podrás usarlas para iniciar sesión y ver tus compras en el futuro.
                    </p>
                  </div>
                </div>

                {/* CTA: Iniciar sesión o ir a Mis Compras */}
                <div className="mt-4 flex flex-wrap gap-3">
                  {!isAuthenticated ? (
                    <button onClick={() => navigate('/login')} className="btn-primary px-5 py-2">
                      Iniciar sesión y ver Mis Compras
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/mis-compras')}
                      className="btn-primary px-5 py-2"
                    >
                      Ir a Mis Compras
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Header */}
      <div className="mb-8 text-center">
        {purchase.payment_status === 'confirmed' ? (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">¡Pago Confirmado!</h1>
            <p className="text-gray-600">
              Tu compra ha sido confirmada. Revisa tu email para ver los tickets.
            </p>
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <svg
                className="h-10 w-10 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">¡Compra Exitosa!</h1>
            <p className="text-gray-600">
              Tu reserva ha sido creada. Sigue las instrucciones para completar el pago.
            </p>
          </>
        )}
      </div>

      {/* Purchase Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Detalles de la Compra</h2>
            <p className="text-sm text-gray-500">ID: {purchase.id}</p>
          </div>
          {getStatusBadge(purchase.payment_status)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Evento</h3>
            <div className="space-y-2 text-sm">
              <p className="font-medium text-gray-900">{purchase.event.name}</p>
              <div className="flex items-center text-gray-600">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {formatDate(purchase.event.event_date)}
              </div>
              <div className="flex items-center text-gray-600">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {purchase.event.venue_name}
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Comprador</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>{purchase.customer_name}</p>
              <p>{purchase.customer_email}</p>
              {purchase.customer_phone && <p>{purchase.customer_phone}</p>}
            </div>
          </div>

          {/* Tickets */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              Asientos ({purchase.tickets.length})
            </h3>
            <div className="space-y-1">
              {purchase.tickets.map((ticket) => (
                <div key={ticket.id} className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {formatSeatIdentifier(ticket.seat.seat_identifier)}
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(ticket.seat.price)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Total</h3>
            <p className="text-3xl font-bold text-blue-600">
              {formatCurrency(purchase.total_amount)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Compra realizada el {formatDate(purchase.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Instructions / Upload Proof */}
      {purchase.payment_status === 'pending' && (
        <>
          <div className="mb-6">
            <PaymentInstructions
              purchaseId={purchase.id}
              totalAmount={purchase.total_amount}
              deadlineHours={purchase.event?.payment_confirmation_deadline_hours ?? null}
              createdAt={purchase.created_at}
              seatCount={purchase.tickets.length}
            />
          </div>

          <ProofUploader
            purchaseId={purchase.id}
            onUploadSuccess={handleProofUpload}
            existingProofUrl={purchase.payment_proof_url}
            existingProofFilename={purchase.payment_proof_filename}
            existingBankReference={purchase.bank_reference}
          />
        </>
      )}

      {/* Confirmed Status */}
      {purchase.payment_status === 'confirmed' && (
        <div className="bg-green-50 rounded-lg border-2 border-green-200 p-6 text-center">
          <svg
            className="h-12 w-12 text-green-600 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-xl font-bold text-green-900 mb-2">¡Tu pago ha sido confirmado!</h3>
          <p className="text-green-700 mb-4">
            Hemos enviado tus tickets con código QR a <strong>{purchase.customer_email}</strong>
          </p>
          <p className="text-sm text-green-600">
            Presenta tu QR code en la entrada del evento para ingresar.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-8 flex justify-center gap-4">
        <button
          onClick={() => navigate('/eventos')}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
        >
          Ver Más Eventos
        </button>
        {purchase.payment_status === 'confirmed' && (
          <button
            onClick={handleDownloadTickets}
            className="btn-primary px-6 py-3 flex items-center gap-2"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Descargar Tickets PDF
          </button>
        )}
      </div>
    </div>
  );
}
